/**
 * Execute a function with a minimum execution time
 * Useful for showing loading states for a consistent duration
 *
 * @param fn - The async function to execute
 * @param minimumTimeMs - Minimum time in milliseconds (default: 3000ms)
 * @returns Promise that resolves after both the function and minimum time
 */
export async function executeWithMinimumTime<T>(
  fn: () => Promise<T>,
  minimumTimeMs: number = 3000
): Promise<T> {
  const startTime = Date.now();

  try {
    // Execute the function
    const result = await fn();

    // Calculate remaining time to meet minimum duration
    const elapsedTime = Date.now() - startTime;
    const remainingTime = Math.max(0, minimumTimeMs - elapsedTime);

    // Wait for remaining time if needed
    if (remainingTime > 0) {
      await new Promise((resolve) => setTimeout(resolve, remainingTime));
    }

    return result;
  } catch (error) {
    // Ensure minimum time even on error
    const elapsedTime = Date.now() - startTime;
    const remainingTime = Math.max(0, minimumTimeMs - elapsedTime);

    if (remainingTime > 0) {
      await new Promise((resolve) => setTimeout(resolve, remainingTime));
    }

    throw error;
  }
}
